import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Avatar,
  Typography,
  Chip,
} from "@mui/material";

import axios from "axios";
import useRequestIdGenerater from "customhooks";
import useTimeStamp from "reuseableTable/uniquetimestamp/GenerateUniqueTimeStamp";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import moment from "moment";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";

function getInitials(name) {
  return name
    ?.split(" ")
    ?.map((part) => part[0])
    ?.join("")
    .toUpperCase();
}

const ReportedByUsers = ({ postId, post }) => {
  const [open, setOpen] = useState(false);
  const [reporters, setReporters] = useState([]);
  const [loading, setLoading] = useState(false);
  const RequestId = useRequestIdGenerater();
  const { rendomRequestId, generateTimeStamp } = useTimeStamp();

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const response = await axios.get(
        `/v1/posts/reported-list-by-post?postId=${postId}&pageSize=10&pageNumber=0`,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem(
              "serviceToken"
            )}`,
            "Content-Type": "application/json",
            requestId: RequestId(
              Math.floor(1000 + Math.random() * 9999),
              Math.floor(1000 + Math.random() * 9999 + rendomRequestId)
            ),
          },
        }
      );
      setReporters(response.data.data?.content || []);
      generateTimeStamp();
    } catch (error) {
      console.error("Error fetching reporters:", error);
    } finally {
      setLoading(false);
      generateTimeStamp();
    }
  };

  const handleClose = () => {
    setOpen(false);
    setReporters([]);
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        size="small"
        variant="text"
        color="inherit"
        startIcon={<ReportProblemOutlinedIcon color="error" />}
      >
        Reported By {post?.reportCount}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: "linear-gradient(145deg, #f5f7fa 0%, #ffffff 100%)",
            boxShadow: "0px 10px 30px rgba(0,0,0,0.1)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1.2rem",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
            py: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ReportProblemOutlinedIcon fontSize="medium" />
          Reported Users List
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress color="error" />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: "none",
                "& .MuiTableCell-root": {
                  py: 1.5,
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: "#f8f9fa",
                      "& th": {
                        fontWeight: "bold",
                        color: "#555",
                        fontSize: "0.875rem",
                      },
                    }}
                  >
                    <TableCell sx={{ width: "5%", pl: 3 }}>#</TableCell>
                    <TableCell sx={{ width: "30%" }}>User</TableCell>
                    <TableCell sx={{ width: "25%" }}>Reported On</TableCell>
                    <TableCell sx={{ width: "40%", pr: 3 }}>Reason</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {reporters?.length > 0 ? (
                    reporters?.map((reporter, i) => (
                      <TableRow
                        key={reporter.userId}
                        hover
                        sx={{
                          "&:nth-of-type(even)": {
                            bgcolor: "#f9f9f9",
                          },
                          "&:last-child td": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <TableCell sx={{ pl: 3, color: "#666" }}>
                          {i + 1}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: "#E3F2FD",
                                fontSize: "0.875rem",
                              }}
                            >
                              {getInitials(reporter.reportByName)}
                            </Avatar>
                            <Box>
                              <Typography fontWeight={500}>
                                {reporter.reportByName}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {reporter.userEmail || "Email not available"}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={moment(reporter.reportedOn).format(
                              "DD MMM YYYY"
                            )}
                            size="small"
                            sx={{
                              bgcolor: "#fff0f0",
                              color: "#ff5252",
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ pr: 3 }}>
                          <Typography
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {reporter.reportReason || "Not specified"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Box textAlign="center" p={2}>
                          <SentimentDissatisfiedIcon
                            sx={{ fontSize: 40, color: "#ddd", mb: 1 }}
                          />
                          <Typography color="text.secondary">
                            No reports found for this post
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #eee" }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderRadius: "20px",
              px: 3,
              borderColor: "#ddd",
              color: "#555",
              "&:hover": {
                borderColor: "#ccc",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReportedByUsers;
